import React, { FunctionComponent } from 'react';
import { useRelayStore } from 'src/relay';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import ApiKeyIconAccountMenu from '../../../shared/svg/ApiKeyIconAccountMenu';
import HelpIcon from '../../../shared/svg/HeadsetAccountMenu';
import SupportIcon from '../../../shared/svg/InfoIconAccountMenu';
import LogoutIcon from '../../../shared/svg/LogoutIconAccountMenu';
import PaymentsIcon from '../../../shared/svg/MoneyIconAccountMenu';
import PermissionsIcon from '../../../shared/svg/PermissionsIconAccountMenu';
import ProductUpdatesIcon from '../../../shared/svg/RocketIconAccountMenu';
import AddonsIcon from '../../../shared/svg/ServerIconAccountMenu';
import PlanIcon from '../../../shared/svg/SlidersIconAccountMenu';
import UserIcon from '../../../shared/svg/UserIconAccountMenu';
import { AccountMenuItem, FooterMenu, MenuAccountContainer, PopupArrow, PopupMenuMiddle } from './globalsToolbar';
import { Logout } from './logout';

export interface InfoUserPopupMenu {
    nameUser: string;
    isMenuHidden: boolean;
    ecommerceName: string;
    userId: number;
    onItemClick: () => void;
}

const UserPopupMenu: FunctionComponent<InfoUserPopupMenu> = (props) => {
    const invalidateRelayStore = useRelayStore((state) => state.invalidateRelayStore);

    const { subscription, sellerMarketplace } = useSubscriptionData();
    const canDisplayTab = sellerMarketplace !== 'MICROSOFT_AZURE' || subscription;

    return (
        <MenuAccountContainer $align={'right'}>
            <PopupArrow />

            <PopupMenuMiddle>
                {!props.isMenuHidden && (
                    <AccountMenuItem
                        icon={<UserIcon />}
                        title={props.nameUser}
                        description={T._('Manage your account details', 'Admin')}
                        href={backofficeUrl('/User')}
                    />
                )}
                {!props.isMenuHidden && canDisplayTab && (
                    <>
                        <AccountMenuItem
                            icon={<PlanIcon />}
                            title={T._('Subscription', 'Admin')}
                            description={T._('Access your subscription details', 'Admin')}
                            href={backofficeUrl('/User/?tab=subscriptions')}
                        />

                        <AccountMenuItem
                            icon={<AddonsIcon />}
                            title={T._('Add-ons', 'Admin')}
                            description={T._('View and activate addons', 'Admin')}
                            href={backofficeUrl('/User/?tab=subscriptions#addons-title')}
                        />

                        <AccountMenuItem
                            icon={<PaymentsIcon />}
                            title={T._('Payments', 'Admin')}
                            description={T._('Manage your payment details', 'Admin')}
                            href={backofficeUrl('/User/?tab=payments')}
                            hasDashedLine={true}
                        />

                        <AccountMenuItem
                            icon={<ApiKeyIconAccountMenu />}
                            title={T._('API Keys', 'Admin')}
                            description={T._('Manage your account API keys', 'Admin')}
                            href={backofficeUrl('/User/?tab=api_keys')}
                            hasDashedLine={true}
                        />

                        <AccountMenuItem
                            icon={<PermissionsIcon />}
                            title={T._('Users and Permissions', 'Admin')}
                            description={T._('Create sub-users for your account', 'Admin')}
                            href={backofficeUrl('/User/?tab=permissions')}
                            hasDashedLine={true}
                        />
                    </>
                )}
                {!props.isMenuHidden && (
                    <AccountMenuItem
                        icon={<ProductUpdatesIcon />}
                        title={T._('Product Updates', 'Admin')}
                        description={T._('Check out our latest product updates', 'Admin')}
                        href='https://www.zakeke.com/resource-hub/release-notes/'
                        hasDashedLine={true}
                    />
                )}
                {!props.isMenuHidden && (
                    <AccountMenuItem
                        icon={<HelpIcon />}
                        title={T._('Get Help', 'Admin')}
                        description={T._('Send us a message to get support', 'Admin')}
                        href='https://zakeke.zendesk.com/hc/en-us/requests/new'
                    />
                )}
                {!props.isMenuHidden && (
                    <AccountMenuItem
                        icon={<SupportIcon />}
                        title={T._('Support Center', 'Admin')}
                        description={T._('Access our knowledge base', 'Admin')}
                        href='https://zakeke.zendesk.com/hc/en-us/sections/360003652420'
                        hasDashedLine={true}
                    />
                )}
                <AccountMenuItem
                    icon={<LogoutIcon />}
                    title={T._('Log out', 'Admin')}
                    titleCss={{ color: '#9F1B1F' }}
                    titleHoverCss={{ color: '#AE4B4E' }}
                    description={T._('Sign out of your account', 'Admin')}
                    descriptionCss={{ color: '#9F1B1F' }}
                    href=''
                    onClick={() => Logout({ invalidateRelayStore })}
                />
            </PopupMenuMiddle>

            <FooterMenu>
                <a href='https://www.zakeke.com/general-conditions-and-privacy/#general-conditions' target='_blank' rel='noopener noreferrer'>
                    {T._('General Conditions', 'Admin')}
                </a>
                <span> • </span>
                <a href='https://www.zakeke.com/general-conditions-and-privacy/#privacy-policy' target='_blank' rel='noopener noreferrer'>
                    {T._('Privacy Policy', 'Admin')}
                </a>
            </FooterMenu>
        </MenuAccountContainer>
    );
};

export default UserPopupMenu;
